import type { ToolCallDetail } from "../../agent-sdk-types.js";
import { deriveToolDetail } from "../tool-detail-registry.js";

/**
 * OpenCode `FilePart` attachment shape (subset we care about).
 * See `@opencode-ai/sdk` `FilePart` — for tool-result images, `url` is a
 * self-contained `data:<mime>;base64,<data>` URL.
 */
type OpencodeAttachmentLike = {
  mime?: unknown;
  url?: unknown;
  filename?: unknown;
};

function parseDataUrl(url: string): { mimeType: string; data: string } | null {
  if (!url.startsWith("data:")) return null;
  const commaIdx = url.indexOf(",");
  if (commaIdx === -1) return null;
  const header = url.slice(5, commaIdx);
  const semiIdx = header.indexOf(";");
  if (semiIdx === -1) return null;
  const mimeType = header.slice(0, semiIdx);
  const flags = header.slice(semiIdx + 1).split(";");
  if (!flags.includes("base64")) return null;
  const data = url.slice(commaIdx + 1);
  if (!mimeType || !data) return null;
  return { mimeType, data };
}

function extractImagesFromAttachments(
  attachments: unknown,
): Array<{ mimeType: string; data: string }> {
  if (!Array.isArray(attachments)) return [];
  const images: Array<{ mimeType: string; data: string }> = [];
  for (const raw of attachments) {
    if (!raw || typeof raw !== "object") continue;
    const att = raw as OpencodeAttachmentLike;
    if (typeof att.mime !== "string" || typeof att.url !== "string") continue;
    if (!att.mime.startsWith("image/")) continue;
    const parsed = parseDataUrl(att.url);
    if (!parsed) continue;
    images.push(parsed);
  }
  return images;
}

export function deriveOpencodeToolDetail(
  toolName: string,
  input: unknown,
  output: unknown,
  attachments?: unknown,
): ToolCallDetail {
  // Pre-pass: image attachments (MCP screenshots etc.) win over name-based dispatch
  // and over text output, mirroring the Claude image pre-pass.
  const images = extractImagesFromAttachments(attachments);
  if (images.length > 0) {
    return { type: "image", toolName, images };
  }
  return deriveToolDetail(toolName, input, output);
}
